import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { filter, map, merge, of } from 'rxjs';
import { buildAdminBreadcrumbs } from '../brightnav-admin-breadcrumb.util';

/**
 * Layout wrapper for the BrightNav administration subapp. Hosts a child router outlet so
 * additional admin routes (for example suggestion CRUD) can be added without changing
 * the BrightNav shell configuration beyond `loadChildren`.
 *
 * @remarks The shell fills the BrightHub main region (`calc(100vh - 3.5rem)`). Routed admin pages
 * scroll inside the shared BDS scroll region below the fixed header and breadcrumb.
 * A PrimeNG breadcrumb reflects the current admin URL; extend {@link buildAdminBreadcrumbs} when
 * new admin child routes are added.
 */
@Component({
  selector: 'bn-brightnav-admin-shell',
  standalone: true,
  imports: [RouterOutlet, Breadcrumb, ScrollRegionComponent],
  templateUrl: './brightnav-admin-shell.component.html',
  styleUrl: './brightnav-admin-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrightnavAdminShellComponent {
  private readonly _router = inject(Router);

  /**
   * Breadcrumb trail for routes under `/brightnav/admin`, updated on each navigation.
   */
  readonly adminBreadcrumbs = toSignal(
    merge(of(null), this._router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))).pipe(
      map((): MenuItem[] => buildAdminBreadcrumbs(this._router.url))
    ),
    { initialValue: buildAdminBreadcrumbs(this._router.url) }
  );
}
